import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { Search, Zap, CheckCircle, AlertCircle, Plus } from "lucide-react";
import { toast } from "sonner";
import type { SitemapEntry } from "@/types/sitemap";

interface StaticPageDiscoveryProps {
  onAddEntries: (
    entries: {
      url: string;
      priority: number;
      change_frequency: string;
      entry_type: string;
    }[]
  ) => Promise<void>;
  existingEntries: SitemapEntry[];
}

interface DiscoveredPage {
  url: string;
  priority: number;
  change_frequency: string;
  category: string;
  description: string;
  exists: boolean;
}

export const StaticPageDiscovery: React.FC<StaticPageDiscoveryProps> = ({
  onAddEntries,
  existingEntries,
}) => {
  const [selectedPages, setSelectedPages] = useState<string[]>([]);
  const [discoveredPages, setDiscoveredPages] = useState<DiscoveredPage[]>([]);
  const [isDiscovering, setIsDiscovering] = useState(false);
  const [manualUrl, setManualUrl] = useState("");
  const [isAddingPages, setIsAddingPages] = useState(false);

  // Common static pages that most websites have
  const commonStaticPages: DiscoveredPage[] = [
    // Main Pages
    {
      url: "/",
      priority: 1.0,
      change_frequency: "daily",
      category: "Main",
      description: "Homepage",
      exists: false,
    },
    {
      url: "/about",
      priority: 0.8,
      change_frequency: "monthly",
      category: "Main",
      description: "About page",
      exists: false,
    },
    {
      url: "/contact",
      priority: 0.7,
      change_frequency: "monthly",
      category: "Main",
      description: "Contact page",
      exists: false,
    },

    // Legal Pages
    {
      url: "/legal/privacy",
      priority: 0.5,
      change_frequency: "yearly",
      category: "Legal",
      description: "Privacy Policy",
      exists: false,
    },
    {
      url: "/legal/terms-of-service",
      priority: 0.5,
      change_frequency: "yearly",
      category: "Legal",
      description: "Terms of Service",
      exists: false,
    },
    {
      url: "/legal/code-of-conduct",
      priority: 0.5,
      change_frequency: "yearly",
      category: "Legal",
      description: "Code of Conduct",
      exists: false,
    },
    {
      url: "/legal/gdpr-compliance",
      priority: 0.5,
      change_frequency: "yearly",
      category: "Legal",
      description: "GDPR Compliance",
      exists: false,
    },

    // Authentication
    {
      url: "/auth",
      priority: 0.6,
      change_frequency: "monthly",
      category: "Auth",
      description: "Authentication page",
      exists: false,
    },
    {
      url: "/login",
      priority: 0.6,
      change_frequency: "monthly",
      category: "Auth",
      description: "Login page",
      exists: false,
    },
    {
      url: "/register",
      priority: 0.6,
      change_frequency: "monthly",
      category: "Auth",
      description: "Registration page",
      exists: false,
    },
    {
      url: "/forgot-password",
      priority: 0.4,
      change_frequency: "monthly",
      category: "Auth",
      description: "Password reset",
      exists: false,
    },

    // User Pages
    {
      url: "/profile",
      priority: 0.7,
      change_frequency: "weekly",
      category: "User",
      description: "User profile",
      exists: false,
    },
    {
      url: "/dashboard",
      priority: 0.8,
      change_frequency: "daily",
      category: "User",
      description: "User dashboard",
      exists: false,
    },
    {
      url: "/verification-process",
      priority: 0.6,
      change_frequency: "monthly",
      category: "User",
      description: "Verification process",
      exists: false,
    },

    // Content Pages
    {
      url: "/polls",
      priority: 0.9,
      change_frequency: "daily",
      category: "Content",
      description: "Polls listing",
      exists: false,
    },
    {
      url: "/insights",
      priority: 0.9,
      change_frequency: "daily",
      category: "Content",
      description: "Insights/Cards listing",
      exists: false,
    },
    {
      url: "/countries",
      priority: 0.8,
      change_frequency: "weekly",
      category: "Content",
      description: "Countries listing",
      exists: false,
    },
    {
      url: "/civic-education",
      priority: 0.7,
      change_frequency: "weekly",
      category: "Content",
      description: "Civic education",
      exists: false,
    },

    // Landing Pages
    {
      url: "/cayman",
      priority: 0.7,
      change_frequency: "monthly",
      category: "Landing",
      description: "Cayman Islands landing",
      exists: false,
    },
    {
      url: "/cayman-enhanced",
      priority: 0.7,
      change_frequency: "monthly",
      category: "Landing",
      description: "Enhanced Cayman landing",
      exists: false,
    },
    {
      url: "/cayman-enhanced-2",
      priority: 0.7,
      change_frequency: "monthly",
      category: "Landing",
      description: "Enhanced Cayman landing v2",
      exists: false,
    },

    // Help & Support
    {
      url: "/help",
      priority: 0.6,
      change_frequency: "monthly",
      category: "Support",
      description: "Help center",
      exists: false,
    },
    {
      url: "/faq",
      priority: 0.6,
      change_frequency: "monthly",
      category: "Support",
      description: "FAQ page",
      exists: false,
    },
    {
      url: "/support",
      priority: 0.6,
      change_frequency: "monthly",
      category: "Support",
      description: "Support page",
      exists: false,
    },

    // Error Pages
    {
      url: "/404",
      priority: 0.1,
      change_frequency: "never",
      category: "Error",
      description: "404 Not Found",
      exists: false,
    },
    {
      url: "/500",
      priority: 0.1,
      change_frequency: "never",
      category: "Error",
      description: "500 Server Error",
      exists: false,
    },
  ];

  useEffect(() => {
    checkExistingPages();
  }, [existingEntries]);

  const checkExistingPages = () => {
    const existingUrls = new Set(existingEntries.map((entry) => entry.url));
    const updatedPages = commonStaticPages.map((page) => ({
      ...page,
      exists: existingUrls.has(page.url),
    }));
    setDiscoveredPages(updatedPages);
  };

  const discoverPagesAutomatically = async () => {
    setIsDiscovering(true);
    try {
      // Simulate checking if pages exist by making HEAD requests
      const pageChecks = await Promise.allSettled(
        commonStaticPages.map(async (page) => {
          try {
            const response = await fetch(window.location.origin + page.url, {
              method: "HEAD",
              cache: "no-cache",
            });
            return {
              ...page,
              exists: existingEntries.some((e) => e.url === page.url),
              available: response.ok,
            };
          } catch {
            return {
              ...page,
              exists: existingEntries.some((e) => e.url === page.url),
              available: false,
            };
          }
        })
      );

      const results = pageChecks
        .filter(
          (result): result is PromiseFulfilledResult<any> =>
            result.status === "fulfilled"
        )
        .map((result) => result.value)
        .filter((page) => page.available && !page.exists);

      if (results.length > 0) {
        toast.success(`Discovered ${results.length} available pages`);
        setDiscoveredPages((prev) =>
          prev.map((page) => {
            const result = results.find((r) => r.url === page.url);
            return result ? { ...page, available: true } : page;
          })
        );
      } else {
        toast.info("No new pages discovered");
      }
    } catch (error) {
      console.error("Error during page discovery:", error);
      toast.error("Failed to discover pages automatically");
    } finally {
      setIsDiscovering(false);
    }
  };

  const handlePageSelection = (url: string, checked: boolean) => {
    setSelectedPages((prev) =>
      checked ? [...prev, url] : prev.filter((p) => p !== url)
    );
  };

  const handleSelectAll = (category: string) => {
    const categoryPages = discoveredPages
      .filter((page) => page.category === category && !page.exists)
      .map((page) => page.url);

    setSelectedPages((prev) => [...new Set([...prev, ...categoryPages])]);
  };

  const handleDeselectAll = (category: string) => {
    const categoryPages = discoveredPages
      .filter((page) => page.category === category)
      .map((page) => page.url);

    setSelectedPages((prev) =>
      prev.filter((url) => !categoryPages.includes(url))
    );
  };

  const addSelectedPages = async () => {
    if (selectedPages.length === 0) {
      toast.error("No pages selected");
      return;
    }

    const pagesToAdd = discoveredPages
      .filter((page) => selectedPages.includes(page.url))
      .map((page) => ({
        url: page.url,
        priority: page.priority,
        change_frequency: page.change_frequency,
        entry_type: "static" as const,
      }));

    if (pagesToAdd.length === 0) {
      toast.error("No valid pages selected");
      return;
    }

    setIsAddingPages(true);
    try {
      await onAddEntries(pagesToAdd);
      toast.success(`Added ${pagesToAdd.length} pages to sitemap`);
      setSelectedPages([]);
      checkExistingPages();
    } catch (error) {
      console.error("Error adding pages:", error);
      toast.error("Failed to add selected pages");
    } finally {
      setIsAddingPages(false);
    }
  };

  const addManualUrl = async () => {
    if (!manualUrl.trim()) {
      toast.error("Please enter a URL");
      return;
    }

    if (!manualUrl.startsWith("/")) {
      toast.error("URL must start with /");
      return;
    }

    try {
      await onAddEntries([
        {
          url: manualUrl.trim(),
          priority: 0.5,
          change_frequency: "weekly",
          entry_type: "static",
        },
      ]);

      toast.success("Manual URL added successfully");
      setManualUrl("");
      checkExistingPages();
    } catch (error) {
      console.error("Error adding manual URL:", error);
      toast.error("Failed to add manual URL");
    }
  };

  const categories = [...new Set(discoveredPages.map((page) => page.category))];
  const selectedCount = selectedPages.length;
  const availablePages = discoveredPages.filter((page) => !page.exists);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            Static Page Discovery
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Identify and add static pages to your sitemap using automated
            discovery or manual entry.
          </p>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="automated" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="automated">Automated Discovery</TabsTrigger>
              <TabsTrigger value="manual">Manual Entry</TabsTrigger>
            </TabsList>

            <TabsContent value="automated" className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium">Common Static Pages</h3>
                  <p className="text-sm text-muted-foreground">
                    Select pages from our curated list of common static pages
                  </p>
                </div>
                <Button
                  onClick={discoverPagesAutomatically}
                  disabled={isDiscovering}
                  className="flex items-center gap-2"
                >
                  <Zap className="h-4 w-4" />
                  {isDiscovering ? "Discovering..." : "Auto-Discover"}
                </Button>
              </div>

              {availablePages.length > 0 && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Found {availablePages.length} pages not yet in your sitemap.
                    {selectedCount > 0 &&
                      ` ${selectedCount} selected for addition.`}
                  </AlertDescription>
                </Alert>
              )}

              <div className="space-y-6">
                {categories.map((category) => {
                  const categoryPages = discoveredPages.filter(
                    (page) => page.category === category
                  );
                  const availableCategoryPages = categoryPages.filter(
                    (page) => !page.exists
                  );

                  if (availableCategoryPages.length === 0) return null;

                  return (
                    <div key={category} className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium flex items-center gap-2">
                          {category} Pages
                          <Badge variant="secondary">
                            {availableCategoryPages.length}
                          </Badge>
                        </h4>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleSelectAll(category)}
                          >
                            Select All
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeselectAll(category)}
                          >
                            Deselect All
                          </Button>
                        </div>
                      </div>

                      <div className="grid gap-2">
                        {categoryPages.map((page) => (
                          <div
                            key={page.url}
                            className="flex items-center space-x-3 p-3 border rounded-lg"
                          >
                            <Checkbox
                              checked={selectedPages.includes(page.url)}
                              onCheckedChange={(checked) =>
                                handlePageSelection(page.url, !!checked)
                              }
                              disabled={page.exists}
                            />
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <code className="text-sm bg-muted px-2 py-1 rounded">
                                  {page.url}
                                </code>
                                {page.exists && (
                                  <Badge
                                    variant="outline"
                                    className="text-green-600"
                                  >
                                    <CheckCircle className="h-3 w-3 mr-1" />
                                    Already Added
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground mt-1">
                                {page.description}
                              </p>
                              <div className="flex gap-4 text-xs text-muted-foreground mt-1">
                                <span>Priority: {page.priority}</span>
                                <span>Frequency: {page.change_frequency}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Fixed Add Selected Pages Button */}
              {selectedCount > 0 && (
                <div className="sticky bottom-0 bg-background border-t pt-4 mt-6">
                  <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                    <div className="flex flex-col gap-1">
                      <span className="font-medium">
                        {selectedCount} page{selectedCount !== 1 ? "s" : ""}{" "}
                        selected
                      </span>
                      <span className="text-sm text-muted-foreground">
                        Ready to add to sitemap
                      </span>
                    </div>
                    <Button
                      onClick={addSelectedPages}
                      disabled={isAddingPages}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      {isAddingPages
                        ? "Adding..."
                        : `Add ${selectedCount} Page${selectedCount !== 1 ? "s" : ""}`}
                    </Button>
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="manual" className="space-y-4">
              <div>
                <h3 className="text-lg font-medium mb-2">Add Custom URL</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Enter any URL that's not in our common pages list
                </p>
              </div>

              <div className="flex gap-2">
                <div className="flex-1">
                  <input
                    type="text"
                    placeholder="/custom-page"
                    value={manualUrl}
                    onChange={(e) => setManualUrl(e.target.value)}
                    className="w-full px-3 py-2 border border-input rounded-md"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    URL must start with / (e.g., /help, /api/docs)
                  </p>
                </div>
                <Button
                  onClick={addManualUrl}
                  className="flex items-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Add URL
                </Button>
              </div>

              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Manual URLs will be added with default settings (priority:
                  0.5, frequency: weekly). You can edit these settings after
                  adding them to the sitemap.
                </AlertDescription>
              </Alert>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};
